import { formatPropertyAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { SEO_CONFIG } from '../config/seo';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import './IDXTownPage.css';
import SEOLinks from './SEOLinks';

const WestminsterHomeValueEstimatePage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [propertyImages, setPropertyImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [isDarkMode]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?city=Westminster&firstPhoto=true&hasMedia=true&limit=9`, {});
        if (response.ok) {
          const data = await response.json();
          const props = data.properties || [];
          setProperties(props);
          props.forEach(p => {
            if (p.listing_key) {
              propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-media/${p.listing_key}`, {}).then(r => r.json()).then(d => {
                const first = (d.media || []).sort((a, b) => parseInt(a.order_hint) - parseInt(b.order_hint))[0];
                const url = first?.media_url || first?.s3_link;
                if (url) setPropertyImages(prev => ({ ...prev, [p.listing_key]: url }));
              }).catch(() => {});
            }
          });
        }
      } catch (error) {
        console.error('Error fetching Westminster properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  return (
    <div className="idxtown-page">
      <Helmet>
        <title>Westminster Home Value Estimate | Free CMA | Truc Doan</title>
        <meta name="description" content="Get a free Westminster home value estimate. Truc Doan provides complimentary Comparative Market Analysis (CMA) reports. Westminster home values are rising 5-8% yearly. Find out what your home is worth today." />
        <meta name="keywords" content="Westminster home value estimate, free CMA Westminster, what is my home worth Westminster, Westminster home appraisal, home value Westminster CA, sell my home Westminster, Westminster real estate market value, Truc Doan home valuation, comparative market analysis Westminster" />
        <link rel="canonical" href={`${SEO_CONFIG.DOMAIN}/westminster-home-value-estimate`} />
        <meta property="og:title" content="Westminster Home Value Estimate | Free CMA | Truc Doan" />
        <meta property="og:description" content="Find out what your Westminster home is worth. Free Comparative Market Analysis from Truc Doan. Home values rising 5-8% yearly." />
        <meta property="og:url" content={`${SEO_CONFIG.DOMAIN}/westminster-home-value-estimate`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={SEO_CONFIG.OG_IMAGE} />
        <meta name="geo.region" content="US-CA" />
        <meta name="geo.placename" content="Westminster, California" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Truc Doan",
            "telephone": SEO_CONFIG.AGENT_PHONE,
            "email": SEO_CONFIG.AGENT_EMAIL,
            "url": `${SEO_CONFIG.DOMAIN}/westminster-home-value-estimate`,
            "image": SEO_CONFIG.OG_IMAGE,
            "description": "Get a free home value estimate for your Westminster property. Truc Doan provides complimentary CMA reports with accurate pricing based on recent sales data.",
            "areaServed": {
              "@type": "City",
              "name": "Westminster, California"
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Westminster",
              "addressRegion": "CA",
              "postalCode": "92683",
              "addressCountry": "US"
            },
            "offers": {
              "@type": "Offer",
              "name": "Free Home Value Estimate",
              "price": "0",
              "priceCurrency": "USD",
              "description": "Complimentary Comparative Market Analysis for Westminster homeowners"
            }
          })}
        </script>
      </Helmet>

      <div className="idxtown-container">
        <button onClick={() => navigate('/')} style={{ position: 'absolute', top: '20px', left: '20px', padding: '10px 20px', background: 'rgb(14, 165, 233)', color: 'rgb(243, 244, 246)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', zIndex: 10 }}>
          {t('nav.home')}
        </button>

        <header className="idxtown-header">
          <h1>Westminster Home Value Estimate</h1>
          <p style={{ marginTop: '16px', fontSize: '18px', opacity: 0.9 }}>
            Find out what your Westminster home is worth — free Comparative Market Analysis from Truc Doan.
          </p>
        </header>

        <section className="cta-section" style={{ marginTop: '0' }}>
          <h2>🏠 Get Your FREE Home Value Estimate Today</h2>
          <p style={{ fontSize: '18px' }}>Westminster home values have been rising 5-8% annually. Your home may be worth more than you think! Contact Truc Doan for a no-obligation, professional home valuation.</p>
          <div className="cta-buttons">
            <a href={`mailto:${SEO_CONFIG.AGENT_EMAIL}?subject=FREE Westminster Home Value Estimate Request`} className="cta-button primary" style={{ fontSize: '18px', padding: '15px 30px' }}>📧 Request Free Estimate</a>
            <a href={`tel:${SEO_CONFIG.AGENT_PHONE}`} className="cta-button secondary" style={{ fontSize: '18px', padding: '15px 30px' }}>📞 Call Now: +1-818-818-0989</a>
          </div>
        </section>

        <section className="idxtown-vibe">
          <h2>What is a Comparative Market Analysis (CMA)?</h2>
          <p>A CMA is a professional evaluation of your home's current market value based on recently sold comparable properties in your area. Unlike automated online estimates that use broad algorithms, a CMA from Truc Doan considers your home's unique features, upgrades, condition, and hyperlocal market conditions in Westminster.</p>
          <p>A professional CMA includes analysis of 3-6 recently sold comparable homes within your neighborhood, adjustments for differences in size, features, and condition, current market trends specific to Westminster, and a recommended listing price range if you're considering selling.</p>
        </section>

        <section className="idxtown-lifestyle">
          <h2>Why Westminster Home Values Are Rising</h2>
          <p>Westminster real estate has seen consistent appreciation driven by multiple factors.</p>
          <div className="amenities-list">
            <div className="amenity">
              <h3>Limited Inventory</h3>
              <p>Westminster is a built-out city with very limited new construction. High demand and low supply continue to push prices upward year over year.</p>
            </div>
            <div className="amenity">
              <h3>Little Saigon Economy</h3>
              <p>The thriving Little Saigon business district brings jobs, commerce, and cultural tourism, making Westminster one of the most economically vibrant cities in Orange County.</p>
            </div>
            <div className="amenity">
              <h3>Central Location</h3>
              <p>Westminster's position between the coast and inland OC, with easy freeway access, makes it desirable for commuters to multiple employment centers.</p>
            </div>
            <div className="amenity">
              <h3>Strong Buyer Demand</h3>
              <p>Vietnamese-American families, first-time buyers, and investors all compete for Westminster properties, keeping the market competitive and prices appreciating.</p>
            </div>
            <div className="amenity">
              <h3>Recent Sales Data</h3>
              <p>Recent comparable sales in Westminster show consistent price increases, with many homes selling above asking price within the first two weeks on market.</p>
            </div>
          </div>
        </section>

        <section className="market-snapshot">
          <h2>Westminster Home Value Market Stats</h2>
          <p>Here's a snapshot of the current Westminster real estate market to give you context for your home's value.</p>
          <table className="market-stats">
            <thead>
              <tr>
                <th>Market Stat</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Median Home Price</td><td>~$750,000</td></tr>
              <tr><td>Average Days on Market</td><td>22 Days</td></tr>
              <tr><td>Annual Appreciation Rate</td><td>5–8%</td></tr>
              <tr><td>Homes Selling Above Asking</td><td>~60%</td></tr>
              <tr><td>Average Sale-to-List Ratio</td><td>101–103%</td></tr>
            </tbody>
          </table>
        </section>

        <section className="properties-section">
          <h2>Recently Listed Westminster Homes (Comparables)</h2>
          {loading ? (
            <p style={{ color: 'rgb(156, 163, 175)' }}>{t('property.loadingProperties')}</p>
          ) : properties.length > 0 ? (
            <div className="properties-grid">
              {properties.slice(0, 9).map((property) => (
                <div key={property.listing_key} className="property-card" onClick={() => navigate(`/listing/${property.listing_key}`)} style={{ backgroundImage: (propertyImages[property.listing_key] || property.first_photo_url) ? `url(${propertyImages[property.listing_key] || property.first_photo_url})` : 'none', backgroundSize: 'cover', backgroundPosition: 'center' }}>
                  <div className="property-price">${property.current_price?.toLocaleString()}</div>
                  <div className="property-address">{formatPropertyAddress(property)}, CA {property.postal_code}</div>
                  <div className="property-type">{property.property_sub_type}</div>
                  {(property.list_agent_full_name || property.list_office_name) && (
                    <div style={{fontSize: '11px', color: '#ddd', marginTop: '2px'}}>{t('landing.listedBy')}: {[property.list_agent_full_name, property.list_office_name].filter(Boolean).join(' | ')}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'rgb(156, 163, 175)' }}>Contact Truc Doan for a detailed analysis of recent sales in your Westminster neighborhood.</p>
          )}
        </section>

        <section className="cta-section">
          <h2>🎯 Ready to Find Out Your Home's Value?</h2>
          <p style={{ fontSize: '18px' }}>Don't rely on inaccurate online estimates. Get a professional, no-obligation home valuation from Truc Doan — a Westminster real estate expert who knows your neighborhood inside and out.</p>
          <div className="cta-buttons">
            <a href={`mailto:${SEO_CONFIG.AGENT_EMAIL}?subject=FREE Westminster Home Value Estimate - Please Contact Me`} className="cta-button primary" style={{ fontSize: '18px', padding: '15px 30px' }}>📧 Get My Free CMA</a>
            <a href={`tel:${SEO_CONFIG.AGENT_PHONE}`} className="cta-button secondary" style={{ fontSize: '18px', padding: '15px 30px' }}>📞 Call: +1-818-818-0989</a>
            <button className="cta-button primary" onClick={() => navigate('/chat')} style={{ border: 'none', cursor: 'pointer', fontSize: '18px', padding: '15px 30px' }}>💬 Chat With Truc</button>
          </div>
          <p style={{ marginTop: '16px', opacity: 0.8 }}>✅ 100% Free &nbsp; ✅ No Obligation &nbsp; ✅ Professional Analysis &nbsp; ✅ Same-Day Response</p>
        </section>
      </div>

      <div style={{ background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/crmlslogo.png" alt="CRMLS" style={{ height: '20px', objectFit: 'contain' }} />
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/equalhousinglogo.png" alt="Equal Housing Opportunity" style={{ height: '20px', objectFit: 'contain' }} />
        <span style={{ fontSize: '10px', color: '#ccc', flex: 1 }}>Based on information from California Regional Multiple Listing Service, Inc. and/or other sources. All data, including all measurements and calculations of area, is obtained from various sources and has not been, and will not be, verified by broker or MLS. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information.</span>
      </div>
      <SEOLinks />
    </div>
  );
};

export default WestminsterHomeValueEstimatePage;
